import type { World } from '../sim/types';
import { SURVEY_LABELS } from './surveyNames';
import { dateLabel, epochDayOf } from '../sim/calendar';
import { PROGRAMS, SURVEY_PROGRAMS, type SurveyResult } from '../sim/monitoring';
import { SURVEY_NAMES, boatJob, boatName, isCleanupHome, isSurveyHome } from '../sim/operations';
import { DEFAULT_AREA_PLAN, areaFindingsHtml, surveyDecisionHtml, attentionOf, type AreaPlan } from './areasPanel';
import { ECONOMY, type SurveyKind } from '../sim/economy';
import type { ReefIsland } from '../sim/reef';
import { declineZ } from '../sim/campaign';
import { displayName, speciesOf } from '../sim/species';
import { goalsOf, inTutorial, recommendedGoal, researchIsland } from './goals';
import { reefActionsHtml } from './reefCard';
import { placesHtml } from './places';
import { stepFromFindings } from './steer';
import { pendingRules } from './operationsPanel';
import { escapeHtml } from './format';
import { coralVerdict, healthSectionHtml, impactSectionHtml } from './reefHealth';
import { communitiesOf, managementOf, speciesRecord, type Indicator } from './overview';
import { speciesHtml } from './overviewPanels';

/**
 * The plain-language pages of the first ten minutes (M262, the user's brief: "introductory reports should first answer:
 * What did we discover? Why does it matter? What can we do about it?"): the coral's report, a survey's findings, the
 * choice of a response, and Manage's home. Each figure is the world's own, read from its monitoring, its operations and
 * its management; the science stays one fold below, as the pages it came from show it. Markup only, from the world.
 */

/** A count against an earlier one, by the log of their ratio over its standard error (each count's error log-normal). */
export interface Change {
  /** The later count over the earlier, less one. */
  ratio: number;
  /** The log ratio in standard errors; ±Infinity where neither count has an error. */
  z: number;
  /** In words: more than, fewer than, or about the same as, beyond two standard errors either way. */
  words: 'more than' | 'fewer than' | 'about the same as';
}

/** Two counts compared (`declineZ`'s measure in `campaign.ts`, the annual review's). */
export function compare(before: { value: number; cv: number | null }, after: { value: number; cv: number | null }): Change {
  const z = declineZ(before, after);
  return { ratio: after.value / before.value - 1, z, words: z >= 2 ? 'more than' : z <= -2 ? 'fewer than' : 'about the same as' };
}

/** Several programs' counts summed (the reef's herbivores together), the sum's error from each's, taken apart. */
export function summed(counts: readonly { value: number; cv: number | null }[]): { value: number; cv: number | null } {
  const value = counts.reduce((a, c) => a + c.value, 0);
  const sd = Math.sqrt(counts.reduce((a, c) => a + (c.cv === null ? 0 : (c.value * c.cv) ** 2), 0));
  return { value, cv: value > 0 && counts.some((c) => c.cv !== null) ? sd / value : null };
}

/** The count's error, in the report's science (M312, the opening loop's G2: "the opening coral report still says 'give or take 17% of that'"). */
function countError(r: SurveyResult): string {
  return r.cv === null ? '' : `<p class="note">The last count is ${dateLabel(r.day)}'s, give or take ${Math.round(100 * r.cv)}% of its value.</p>`;
}

/** The grazing fish's fishing threat in plain words ("fished: 6 species, up to 20% of the sizes taken caught a year"). */
function fishingWords(threat: string): string {
  const m = /^fished: (\d+) species, up to (\d+)%/.exec(threat);
  return m ? `Fishers take ${m[1]} of these species, up to ${m[2]}% a year of the sizes they keep.` : 'Fishers take these fish here.';
}

const percent = (x: number): string => `${x > 0 ? '+' : ''}${Math.round(100 * x)}%`;

/** A page's three plain questions, then its science folded. */
function report(title: string, found: string, matters: string, next: string, details: string): string {
  return `<h3>${escapeHtml(title)}</h3>
    <section class="report"><h4>What we found</h4>${found}<h4>Why it matters</h4><p>${escapeHtml(matters)}</p><h4>What we can do</h4>${next}</section>
    ${details ? `<details class="science"><summary>Scientific details</summary>${details}</details>` : ''}`;
}

/** Why each surveyed group matters, in a sentence (each the record's own: what its community does on the reef). */
const MATTERS = {
  coral: 'Living coral builds the reef itself: the shelter its fish live in and the base of its food web. Algae and heat are what kill it.',
  herbivores: 'Parrotfish (uhu), unicornfish (kala), chubs (nenue) and convict tangs (manini) graze the algae that compete with coral for space on the reef.',
  deep7: 'The Deep 7 are the bottomfish Hawaiʻi\'s fishers prize most; their counts show how hard the deep slope is fished.',
} as const;

/** The coral's counts up to today, oldest first. */
const coralCounts = (world: World): SurveyResult[] => (world.monitoring.coral ?? []).filter((r) => r.day <= epochDayOf(world));

/**
 * The coral's report (M262: the campaign's "Read the coral report"): the live cover last counted and how sure, against
 * the count before it where there is one; why it matters; and the way on, the reef's fish surveyed. The coral's species
 * page below it.
 */
export function coralReportHtml(world: World, plan: AreaPlan = DEFAULT_AREA_PLAN): string {
  const counts = coralCounts(world);
  const last = counts.at(-1);
  // The reef's own record (M327, one reef: the islands' counts weighted by their reef, `reefCoralHistory`; M326's fix
  // had made it the island in the selector, which is gone); the sanctuary's one monitoring site's count kept in the
  // science fold (M319).
  const name = 'Maui Nui Reef';
  const v = coralVerdict(world, 'reef');
  const whose = (p: { by: 'noaa' | 'conservancy'; year: number; day: number }): string => (p.by === 'noaa' ? `NOAA's divers, ${p.year}` : `your survey, ${dateLabel(p.day)}`);
  const trend = !v.last ? '' : !v.against ? '<p>It is the only count of its reef so far, so there is no trend yet.</p>'
    : v.trend === 'down' ? `<p>It was ${v.against.value.toFixed(0)}% in ${v.against.year}: a fall the counts' uncertainty cannot explain. Whether it is still falling, only a new count can tell.</p>`
    : v.trend === 'up' ? `<p>It was ${v.against.value.toFixed(0)}% in ${v.against.year}: a rise past the counts' uncertainty.</p>`
    : `<p>About the same as in ${v.against.year} (${v.against.value.toFixed(0)}%): no change the counts can tell apart.</p>`;
  const found = v.last
    ? `<p class="headline">About <strong>${v.last.value.toFixed(0)}%</strong> of ${escapeHtml(name)}'s floor is live coral (${escapeHtml(whose(v.last))}).</p>${trend}`
    : `<p>${escapeHtml(name)}'s coral has not been counted yet.</p>`;
  // A survey planned counts as sent (M291's review: with it waiting, each press of the decision had queued another).
  // A shore cleanup is no survey (M307): the decision stays until a survey is sent.
  const surveyed = world.operations.jobs.some((j) => j.kind === 'survey' && j.survey !== 'shoreCleanup') || world.operations.finished.some(isSurveyHome) || world.operations.plan.some((p) => p.kind !== 'shoreCleanup');
  const next = surveyed
    ? '<p>Your own surveys add counts of their own. Keep counting: one count is a snapshot, several make a trend.</p>'
    // The opening's one route to its first survey (M291, the roadmap's R1: this button had opened the old survey page,
    // preset to the fish census, beside M289's choice).
    : surveyDecisionHtml(world, plan);
  return report('The coral report', found, MATTERS.coral, next, (last ? countError(last) : '') + speciesHtml(speciesRecord(world, 'coral')));
}

/** The kind of a finished survey, read from its title ("Reef fish census finished"). */
function kindOf(title: string): SurveyKind | null {
  return (Object.keys(SURVEY_NAMES) as SurveyKind[]).find((k) => title.startsWith(SURVEY_NAMES[k])) ?? null;
}

/** The latest survey home, its kind and its day; none before the first. */
export function lastSurvey(world: World): { kind: SurveyKind; day: number } | null {
  // A survey's, never a shore cleanup's (M307: it counts nothing).
  const f = world.operations.finished.filter((x) => isSurveyHome(x) && x.endDay <= epochDayOf(world)).at(-1);
  const kind = f ? kindOf(f.title) : null;
  return f && kind ? { kind, day: f.endDay } : null;
}

/** Each program a survey counted on its day, with the count before it (NOAA's, or the conservancy's own). */
export function surveyCounts(world: World, kind: SurveyKind, day: number): { id: string; name: string; now: SurveyResult; before: SurveyResult | null }[] {
  return SURVEY_PROGRAMS[kind].flatMap((id) => {
    const list = world.monitoring[id] ?? [];
    const now = list.find((r) => r.day === day);
    if (!now) return [];
    return [{ id, name: id === 'coral' ? 'Live coral' : displayName(speciesOf(id)), now, before: list.filter((r) => r.day < day).at(-1) ?? null }];
  });
}

/**
 * A survey's findings (M262: the campaign's "Read the survey's findings"): what it counted against the count before
 * it, each within its errors (the reef's grazing fish summed, the coral, the Deep 7 summed); why it matters; the way to
 * a response. Every count below, each with its error and the count it is set against. Nothing said a count does not
 * show: a difference inside two standard errors is said to be within the counts' uncertainty.
 */
export function findingsHtml(world: World): string {
  // A shore cleanup the boat's last trip home (M307): what it removed first, then the last survey's findings, which the
  // campaign's "Read the survey's findings" still reads (`lastSurvey`).
  const trip = world.operations.finished.filter((x) => x.kind === 'survey' && x.endDay <= epochDayOf(world)).at(-1);
  const cleanup = trip && isCleanupHome(trip) ? areaFindingsHtml(world, trip) : '';
  return cleanup && !lastSurvey(world) ? cleanup : cleanup + surveyFindingsHtml(world);
}

function surveyFindingsHtml(world: World): string {
  // A survey of one island, or a bleaching assessment, counts no sanctuary-wide program: its findings are its island's
  // (M274; review M274's: it read "0 counts", an assessment as the Deep 7's).
  const home = world.operations.finished.filter((x) => isSurveyHome(x) && x.endDay <= epochDayOf(world)).at(-1);
  // A fish count's too, one report for the trip whichever page opens it (M336, C1: the findings had told the program's
  // tonnes against NOAA's 2024 count beside the trip's own "first survey").
  if (home && (home.island !== null || kindOf(home.title) === 'reefBleaching' || kindOf(home.title) === 'reefFish')) return areaFindingsHtml(world, home, stepFromFindings(world));
  const last = lastSurvey(world);
  if (!last) {
    const job = boatJob(world.operations);
    return `<h3>Findings</h3><p class="note">${job?.survey ? `The ${escapeHtml(boatName(job.boat))} is at sea; its findings come home ${escapeHtml(dateLabel(job.phaseEnds[0] as number))}.` : 'No survey has come home yet. Send the boat to count the reef first.'}</p>`;
  }
  const rows = surveyCounts(world, last.kind, last.day);
  // A fish trip has its own report since M336 (`areaFindingsHtml`): the program's sum here is the coral's or the Deep 7's.
  const group = rows;
  const both = group.filter((r) => r.before !== null);
  const label = last.kind === 'reefFish' ? 'the reef\'s grazing fish (parrotfish, kala, nenue and manini)' : last.kind === 'reefBenthic' ? 'live coral' : 'the Deep 7';
  const unit = last.kind === 'reefBenthic' ? '% of the reef\'s floor' : ' metric tons';
  const fmt = (x: number): string => (last.kind === 'reefBenthic' ? x.toFixed(1) : x >= 10 ? x.toFixed(0) : x.toFixed(1));
  let found: string;
  if (both.length === 0) found = `<p>The ${escapeHtml(SURVEY_LABELS[last.kind].toLowerCase())} came home ${escapeHtml(dateLabel(last.day))} with ${rows.length} ${rows.length === 1 ? 'count' : 'counts'}: the first of their kind, so there is nothing to compare them with yet.</p>`;
  else {
    const now = summed(both.map((r) => r.now));
    const was = summed(both.map((r) => r.before as SurveyResult));
    const change = compare(was, now);
    const lastDay = Math.max(...both.map((r) => (r.before as SurveyResult).day));
    const much = last.kind === 'reefBenthic';
    const lead = change.words === 'more than' ? (much ? 'More' : 'More of') : change.words === 'fewer than' ? (much ? 'Less' : 'Fewer of') : (much ? 'About as much' : 'About as many of');
    found = `<p class="headline"><strong>${lead}</strong> ${escapeHtml(label)} ${change.words === 'about the same as' ? 'as' : 'than'} at the last count.</p>
      <p>${escapeHtml(fmt(now.value))}${escapeHtml(unit)} now, against ${escapeHtml(fmt(was.value))}${escapeHtml(unit)} at the last count (${escapeHtml(dateLabel(lastDay))}), ${escapeHtml(percent(change.ratio))}${Math.abs(change.z) < 2 ? ': within the counts\' uncertainty, so no change can be told yet' : ''}.</p>`;
  }
  const herbivores = communitiesOf(world).find((c) => c.id === 'reefHerbivores');
  const fishing = herbivores?.threats.find((t) => t.startsWith('fished'));
  const m = managementOf(world);
  // What the findings change (M330, O5): the step they call for, why, and its way (lit on the reef's card); else the
  // standing words.
  const step = stepFromFindings(world);
  const steer = step && step.day === last.day ? `<p class="steer"><strong>Next:</strong> ${escapeHtml(step.why)}</p><button class="primary" data-act="goal" data-goal="${escapeHtml(step.act)}">${escapeHtml(step.label)}</button>` : null;
  const next = steer ?? (last.kind === 'slopeCamera'
    ? '<p>The conservancy\'s rules and patrols act on the reef\'s fish, not on the Deep 7\'s fishing: these counts are for watching the slope.</p><button class="primary" data-act="open" data-kind="overview">See the reef\'s options</button>'
    : last.kind === 'reefBenthic'
    ? '<p>Coral responds to heat and to the algae its grazers keep down. Protecting the grazing fish is the lever the conservancy holds.</p><button class="primary" data-act="open" data-kind="overview">See the options</button>'
    : `<p>${fishing ? `${escapeHtml(fishingWords(fishing))} ` : ''}${m?.islands.every((x) => x.herbivoresProtected) ? 'The reef\'s grazing fish are already protected; patrols keep more of the rule.' : 'The conservancy can propose a rule that protects them, or pay for patrols that keep the rules already made.'}</p><button class="primary" data-act="open" data-kind="overview">See the options</button>`);
  const table = rows.map((r) => `<li class="manage-row"><span class="row-name">${escapeHtml(r.name)}</span><span class="row-note">${escapeHtml(fmt(r.now.value))}${escapeHtml(unit)}${r.now.cv === null ? '' : ` ± ${escapeHtml(fmt(r.now.value * r.now.cv))}`}${r.before ? `, against ${escapeHtml(fmt(r.before.value))} on ${escapeHtml(dateLabel(r.before.day))} (${escapeHtml(compare(r.before, r.now).words)})` : ', the first count'}</span></li>`).join('');
  const how = PROGRAMS[rows[0]?.id ?? '']?.source;
  const details = `<p class="note">Each count with its standard error. Two counts differ only beyond two standard errors of the log of their ratio.${how ? ` How it is counted: ${escapeHtml(how)}` : ''}</p><ul class="rows">${table}</ul>`;
  return report(`${SURVEY_LABELS[last.kind]}: findings`, found, last.kind === 'reefBenthic' ? MATTERS.coral : last.kind === 'reefFish' ? MATTERS.herbivores : MATTERS.deep7, next, details);
}

/** The sanctuary's main concern of the moment, in a few words and its detail (M262: "The most important current ecological concern"). */
function mainConcern(world: World, indicators: readonly Indicator[]): { what: string; detail: string } | null {
  const reef = indicators.find((i) => i.id === 'reef');
  // At the objective card's own heat stress, Alert level 1 or more, the threats' level (review M267's: a watch or a
  // warning made it the concern here and nothing on the card, and the reef's words were read for it).
  const acute = indicators.find((i) => i.id === 'threats')?.level === 'poor';
  if (acute && reef) return { what: 'Heat stress on the coral', detail: reef.why };
  const herbivores = communitiesOf(world).find((c) => c.id === 'reefHerbivores');
  const fishing = herbivores?.threats.find((t) => t.startsWith('fished'));
  if (fishing) return { what: 'Fishing of the reef\'s grazing fish', detail: fishingWords(fishing) };
  const threats = indicators.find((i) => i.id === 'threats');
  return threats && threats.level !== 'good' ? { what: 'Threats at work', detail: threats.why } : null;
}

/**
 * Manage's home (M262; M274, the redesign's section 9: "Needs attention, Current operations, Research, Protect, Build,
 * Review"): what needs attention now; the sanctuary's map, an island a press away; the work under way in a few lines
 * (the boat and its plan, the rules in process, the patrols, the money); and the four ways in, Research (the island the
 * record most asks about), Protect (the island chosen), Build (the second boat, M277), Review (the month's report). The
 * rest a press away under More.
 */
export function homeHtml(world: World, indicators: readonly Indicator[], areaPlan: AreaPlan = DEFAULT_AREA_PLAN, patrolStarts: Partial<Record<ReefIsland, number>> = {}): string {
  const ops = world.operations;
  const concern = mainConcern(world, indicators);
  // The findings offered once a survey is home (M262's review: offered before, they had nothing to read).
  const surveyed = ops.finished.some((f) => f.kind === 'survey');
  const managed = managementOf(world) !== null;
  // The record's falls and the barely known are the reef health tiles' now (M287): the rest asks a choice.
  const attention = attentionOf(world, areaPlan).filter((a) => a.id !== 'record' && a.id !== 'unknown');
  // The ways in kept a fold away (M287: the home is three sections; the rest is a press away); the reef's own actions
  // its card's above them, the work done and the goals' priorities folded beneath (M327, one reef. GAME CHOICE: the
  // priorities' ways are the reef card's own buttons again, and the objective card carries the one recommended; past the
  // first chapter they had put the home at 325 words, MEASURED by `npm run review:cards`, against M296's 150).
  const actions = [
    managed ? '' : `<button data-act="open" data-kind="survey"><strong>Research</strong><span>Send the ${escapeHtml(ECONOMY.boat.name)} to count the reef</span></button>`,
    managed ? `<button data-act="open" data-kind="build"><strong>Build</strong><span>${world.operations.projects.length > 0 ? `The ${escapeHtml(ECONOMY.secondBoat.name)}: ${world.operations.boats > 1 ? 'in the water' : 'ordered'}` : 'A second boat'}</span></button>` : '',
    `<button data-act="open" data-kind="report"><strong>Review</strong><span>${ops.months.length > 0 ? 'The month\'s report: done, traded, unresolved' : 'The first month\'s report, on its last day'}</span></button>`,
  ].filter(Boolean).join('');
  const urgent = attention.length > 0
    ? `<ul class="attention">${attention.map((a) => `<li class="tone-${a.tone}">${a.html}</li>`).join('')}</ul>`
    : concern && !managed ? `<ul class="attention"><li class="tone-info"><strong>${escapeHtml(concern.what)}:</strong> <span>${escapeHtml(concern.detail)}</span></li></ul>` : '';
  return `${managed ? reefActionsHtml(world, areaPlan, pendingRules(world), patrolStarts) : ''}
    ${managed ? placesHtml(world) : ''}
    ${managed ? `<details class="home-more"><summary>The reef's health</summary>${healthSectionHtml(world)}</details>` : ''}
    ${urgent ? `<section class="home-section">${urgent}</section>` : ''}
    ${managed ? `<details class="home-more"><summary>Your work and the next priorities</summary>${impactSectionHtml(world)}<section class="home-section"><h3>Next priorities</h3>${prioritiesHtml(world)}</section></details>` : ''}
    <details class="home-more"><summary>Operations and reports</summary>
      <p class="more"><button class="link" data-act="open" data-kind="plan">Operations: the boat's plan and the work under way</button></p>
      <div class="home-actions">${actions}</div>
      <p class="more">
      ${surveyed ? '<button class="link" data-act="open" data-kind="findings">The last survey\'s findings</button>' : ''}
      <button class="link" data-act="open" data-kind="details">Sanctuary details</button>
      <button class="link" data-act="open" data-kind="operations">Staff and accounts</button>
      <button class="link" data-act="open" data-kind="campaign">The campaign</button>
      ${managed ? '<button class="link" data-act="open" data-kind="management">Every rule and patrol</button>' : ''}
      <button class="link" data-act="open" data-kind="conditions">Conditions</button>
      <button class="link" data-act="open" data-kind="guide">Field guide</button>
      </p></details>`;
}

/**
 * The goals as cards (M287, the brief's 3.1 "Next priorities" and 7.2 to 7.3): each not met with a way now, its problem
 * in a sentence, what it costs and what choosing it gives up, the recommended one marked; held back during the first
 * chapter's guidance (review M278's: two "do this now"s at once).
 */
export function prioritiesHtml(world: World): string {
  const goals = goalsOf(world);
  if (goals.length === 0 || inTutorial(world)) return '';
  const rec = recommendedGoal(goals);
  const open = goals.filter((g) => g.level !== 'met' && g.way);
  const met = goals.filter((g) => g.level === 'met');
  const cards = open.map((g) => `<li class="priority${g === rec ? ' recommended' : ''}">
    <strong>${escapeHtml(g.title)}</strong>${g === rec ? ' <span class="chip tone-info">Recommended</span>' : ''}
    <span class="priority-problem">${escapeHtml(g.state)}</span>
    ${g.cost ? `<span class="priority-cost">Costs: ${escapeHtml(g.cost)}</span>` : ''}
    ${g.gives ? `<span class="priority-gives">Gives up: ${escapeHtml(g.gives)}</span>` : ''}
    <button class="${g === rec ? 'primary' : ''}" data-act="goal" data-goal="${escapeHtml((g.way as { act: string }).act)}">${escapeHtml((g.way as { label: string }).label)}</button></li>`).join('');
  return `${cards ? `<ul class="priorities">${cards}</ul>` : '<p class="note">Every goal is met or waits on the days: let them run, or watch the reef.</p>'}
    ${met.length > 0 ? `<p class="row-note">Met: ${escapeHtml(met.map((g) => g.title.toLowerCase()).join('; '))}.</p>` : ''}`;
}

export { researchIsland };

